'use strict';

import { SharedAngular } from '@Client/@types/sharedAngular';
import { IQService } from 'angular';
import { PublicType } from '@Shared.Angular/@types/core/contracts/constants/publicType';

angular
  .module('flowingly.runner.library')
  .controller('flowModelOwnerDialogController', flowModelOwnerDialogController);

flowModelOwnerDialogController.$inject = [
  '$scope',
  '$q',
  'APP_CONFIG',
  'flowModelOwnerDialogService',
  'userApiService',
  'flowinglyConstants',
  'brandingService',
  'permissionsService'
];

function flowModelOwnerDialogController(
  $scope: IScopeNgDialog,
  $q: IQService,
  APP_CONFIG: AppConfig,
  flowModelOwnerDialogService: FlowModelOwnerDialogService,
  userApiService: SharedAngular.UserApiService,
  flowinglyConstants: SharedAngular.FlowinglyConstants,
  brandingService: SharedAngular.BrandingService,
  permissionsService: SharedAngular.PermissionsService
) {
  const ctrl = this;

  const PUBLIC_MAP_PATH = '/map/';
  const PUBLIC_FORM_PATH = '/form/';
  const IFRAME_WIDTH = 650;
  const IFRAME_HEIGHT = 800;

  ctrl.submitOwnersAndShareSettings = submitOwnersAndShareSettings;
  ctrl.isMapPubliclyShareable = $scope.ngDialogData.isPublic;
  ctrl.publicType = $scope.ngDialogData.publicType || PublicType.Public;
  ctrl.selectedFlowModelOwnerList = [];
  ctrl.selectedReportOwnerList = [];
  ctrl.flowModelId = $scope.ngDialogData.flowModelId;
  ctrl.flowModelName = $scope.ngDialogData.flowModelName;
  ctrl.isProcessMapType = $scope.ngDialogData.isProcessMapType;
  ctrl.hasPublicForm =
    !ctrl.isProcessMapType && $scope.ngDialogData.publishedTo === 'Public User';

  ctrl.disabledPublicSharingMessage =
    'Public sharing is not enabled for this process map.';
  ctrl.hasReportOwnerUpdatePermission =
    permissionsService.currentUserHasPermission(
      flowinglyConstants.permissions.FLOWMODEL_REPORTOWNER_UPDATE
    );
  ctrl.hasFlowmodelOwnerUpdatePermission =
    permissionsService.currentUserHasPermission(
      flowinglyConstants.permissions.FLOWMODEL_OWNER_UPDATE
    );

  ctrl.helpUri = brandingService.getBrandedHelpUri(
    'https://help.flowingly.net/administration/flow-model-owner'
  );

  ctrl.showTabGroups = showTabGroups;
  ctrl.showPublicSharing = showPublicSharing;
  ctrl.publicTypePublic = PublicType.Public;
  ctrl.publicTypeIntranet = PublicType.Intranet;
  ctrl.expandGroupMembersFilter = (user, roles) => {
    return (
      user &&
      roles.some((role) =>
        user.roles.some((userRole) => userRole.name === role)
      )
    );
  };

  ctrl.cancel = () => {
    $scope.closeThisDialog();
  };

  ctrl.enableReportOwner = APP_CONFIG.enableReportOwner;
  ctrl.enablePublicProcessMaps = APP_CONFIG.enablePublicProcessMaps;

  if (ctrl.isProcessMapType) {
    ctrl.publicShareableUrl = `${window.location.origin}${PUBLIC_MAP_PATH}${ctrl.flowModelId}`;
  } else if (!ctrl.isProcessMapType && ctrl.hasPublicForm) {
    ctrl.publicShareableUrl = `${APP_CONFIG.runnerUrl}${PUBLIC_FORM_PATH}${ctrl.flowModelId}`;
  }

  ctrl.iframeTag = `<iframe width="${IFRAME_WIDTH}" height="${IFRAME_HEIGHT}" src="${ctrl.publicShareableUrl}" frameborder="0"></iframe>`;

  ctrl.publicShareableDisplayValue = ctrl.isMapPubliclyShareable
    ? ctrl.publicShareableUrl
    : ctrl.disabledPublicSharingMessage;
  ctrl.embedCodeDisplayValue = ctrl.isMapPubliclyShareable
    ? ctrl.iframeTag
    : ctrl.disabledPublicSharingMessage;

  $scope.$on('kendoWidgetCreated', function (ev, widget) {
    if (widget === ctrl.tabStrip) {
      ctrl.tabStrip.select(0);
    }
  });

  ctrl.onToggleChange = () => {
    const publicShareableTextAreaContainer =
      document.getElementById('publicUrlContainer');
    const embedCodeTextAreaContainer =
      document.getElementById('embedCodeContainer');

    if (ctrl.isMapPubliclyShareable) {
      if (publicShareableTextAreaContainer) {
        publicShareableTextAreaContainer.classList.remove(
          'public-sharing-disabled'
        );
      }
      if (embedCodeTextAreaContainer) {
        embedCodeTextAreaContainer.classList.remove('public-sharing-disabled');
      }
      ctrl.publicShareableDisplayValue = ctrl.publicShareableUrl;
      ctrl.embedCodeDisplayValue = ctrl.iframeTag;
    } else {
      if (publicShareableTextAreaContainer) {
        publicShareableTextAreaContainer.classList.add(
          'public-sharing-disabled'
        );
      }
      if (embedCodeTextAreaContainer) {
        embedCodeTextAreaContainer.classList.add('public-sharing-disabled');
      }
      ctrl.publicShareableDisplayValue = ctrl.embedCodeDisplayValue =
        ctrl.disabledPublicSharingMessage;
    }
  };

  init();

  function init() {
    ctrl.loading = true;
    const flowModelOwnersPromise = getFlowModelOwnerUsers();
    const reportOwnersPromise = getReportOwnerUsers();
    const existingFlowModelOwnersPromise = loadExistingFlowModelOwners();
    const existingReportOwnersPromise = loadExistingReportOwners();

    $q.all([
      flowModelOwnersPromise,
      reportOwnersPromise,
      existingFlowModelOwnersPromise,
      existingReportOwnersPromise
    ]).then(() => (ctrl.loading = false));
  }

  function submitOwnersAndShareSettings() {
    const flowModelAdminUserIds = ctrl.selectedFlowModelOwnerList
      .filter(
        (item) =>
          item.searchEntityType === flowinglyConstants.searchEntityType.USER
      )
      .map((item) => item.id);
    const flowModelAdminGroupIds = ctrl.selectedFlowModelOwnerList
      .filter(
        (item) =>
          item.searchEntityType === flowinglyConstants.searchEntityType.GROUP
      )
      .map((item) => item.id);
    const reportAdminUserIds = ctrl.selectedReportOwnerList
      .filter(
        (item) =>
          item.searchEntityType === flowinglyConstants.searchEntityType.USER
      )
      .map((item) => item.id);
    const reportAdminnGroupIds = ctrl.selectedReportOwnerList
      .filter(
        (item) =>
          item.searchEntityType === flowinglyConstants.searchEntityType.GROUP
      )
      .map((item) => item.id);

    const promises = [];

    if (ctrl.hasReportOwnerUpdatePermission && ctrl.enableReportOwner) {
      promises.push(
        flowModelOwnerDialogService.saveReportOwners({
          flowModelId: ctrl.flowModelId,
          userIds: reportAdminUserIds,
          groupIds: reportAdminnGroupIds
        })
      );
    }

    if (ctrl.hasFlowmodelOwnerUpdatePermission) {
      promises.push(
        flowModelOwnerDialogService.saveFlowModelOwners({
          flowModelId: ctrl.flowModelId,
          userIds: flowModelAdminUserIds,
          groupIds: flowModelAdminGroupIds
        })
      );
    }

    if (showPublicSharing()) {
      promises.push(
        flowModelOwnerDialogService.modifyPublicMapSharing(
          ctrl.flowModelId,
          ctrl.isMapPubliclyShareable,
          ctrl.publicType
        )
      );
    }

    $q.all(promises).then(() => {
      $scope.closeThisDialog({
        success: true,
        publicType: ctrl.isMapPubliclyShareable ? ctrl.publicType : null
      });
    });
  }

  function getFlowModelOwnerUsers() {
    return userApiService
      .getUsersWithPermission(
        flowinglyConstants.permissions.FLOWMODEL_OWNER_CANDIDATE
      )
      .then((users) => {
        ctrl.flowModelOwnerUsers = users.sort((a, b) =>
          a.fullName.localeCompare(b.fullName)
        );
      });
  }

  function getReportOwnerUsers() {
    if (!ctrl.enableReportOwner) {
      return $q.defer().resolve();
    }

    return userApiService
      .getUsersWithPermission(
        flowinglyConstants.permissions.FLOWMODEL_REPORTOWNER_CANDIDATE
      )
      .then(
        (users) =>
          (ctrl.reportOwnerUsers = users.sort((a, b) =>
            a.fullName.localeCompare(b.fullName)
          ))
      );
  }

  function loadExistingFlowModelOwners() {
    return flowModelOwnerDialogService
      .getExistingFlowModelOwners(ctrl.flowModelId)
      .then(function (response) {
        if (response?.data) ctrl.selectedFlowModelOwnerList = response.data;
      });
  }

  function loadExistingReportOwners() {
    if (!ctrl.enableReportOwner) return $q.defer().resolve();

    return flowModelOwnerDialogService
      .getExistingReportOwners(ctrl.flowModelId)
      .then(function (response) {
        if (response?.data) ctrl.selectedReportOwnerList = response.data;
      });
  }

  function showTabGroups() {
    if (ctrl.isProcessMapType) {
      return ctrl.enablePublicProcessMaps || ctrl.enableReportOwner;
    }
    return ctrl.enableReportOwner || showPublicForm();
  }

  function showPublicSharing() {
    return ctrl.isProcessMapType && ctrl.enablePublicProcessMaps;
  }

  function showPublicForm() {
    return !ctrl.isProcessMapType && ctrl.hasPublicForm;
  }
  ctrl.showPublicForm = showPublicForm;
}
